/**
 * CollectibleSystem - Pure logic system for collectible letters/numbers management.
 *
 * Manages collection state for letters (A-Z) and numbers (1-9), tracks power-up
 * trigger conditions (vowels complete, numbers complete, alphabet complete),
 * and emits events for integration with rendering and power-up systems.
 *
 * Pure logic — no Phaser dependencies. Designed to be testable in isolation.
 *
 * Requirements: 7.3, 8.1, 8.4, 9.1, 10.1, 10.3
 */

import type { CollectibleSaveData } from '../types/progress.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CollectibleType = 'letter' | 'number';

export interface CollectibleItem {
  id: string;           // e.g. "letter-A", "number-3"
  type: CollectibleType;
  value: string;        // "A"-"Z" or "1"-"9"
  collected: boolean;
}

export type CollectibleEvent = 'collected' | 'vowels-complete' | 'numbers-complete' | 'alphabet-complete';
export type CollectibleEventHandler = (...args: any[]) => void;

export interface CollectibleEventData {
  event: CollectibleEvent;
  item?: CollectibleItem;
}

export interface ICollectibleSystem {
  // State
  readonly letters: ReadonlyMap<string, boolean>;   // A-Z → collected?
  readonly numbers: ReadonlyMap<string, boolean>;   // 1-9 → collected?
  readonly vowelsComplete: boolean;
  readonly numbersComplete: boolean;
  readonly alphabetComplete: boolean;

  // Actions
  collect(id: string): void;
  reset(): void;

  // Events
  on(event: CollectibleEvent, handler: CollectibleEventHandler): void;
  off(event: CollectibleEvent, handler: CollectibleEventHandler): void;

  // Persistence
  serialize(): CollectibleSaveData;
  deserialize(data: CollectibleSaveData): void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const VOWELS = ['A', 'E', 'I', 'O', 'U'];
const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

// ─── Implementation ───────────────────────────────────────────────────────────

/**
 * CollectibleSystem implementation.
 * Pure logic — no Phaser dependencies.
 */
export class CollectibleSystem implements ICollectibleSystem {
  private _letters: Map<string, boolean> = new Map();
  private _numbers: Map<string, boolean> = new Map();
  private _vowelsComplete: boolean = false;
  private _numbersComplete: boolean = false;
  private _alphabetComplete: boolean = false;
  private eventListeners: Map<CollectibleEvent, Set<CollectibleEventHandler>> = new Map();

  constructor() {
    this.initializeMaps();
    this.eventListeners.set('collected', new Set());
    this.eventListeners.set('vowels-complete', new Set());
    this.eventListeners.set('numbers-complete', new Set());
    this.eventListeners.set('alphabet-complete', new Set());
  }

  // ─── State Accessors ────────────────────────────────────────────────────────

  get letters(): ReadonlyMap<string, boolean> {
    return this._letters;
  }

  get numbers(): ReadonlyMap<string, boolean> {
    return this._numbers;
  }

  get vowelsComplete(): boolean {
    return this._vowelsComplete;
  }

  get numbersComplete(): boolean {
    return this._numbersComplete;
  }

  get alphabetComplete(): boolean {
    return this._alphabetComplete;
  }

  // ─── Actions ────────────────────────────────────────────────────────────────

  /**
   * Collect an item by ID (e.g. "letter-A", "number-3").
   * Idempotent: collecting an already-collected item is a no-op.
   * After marking as collected, checks and emits power-up trigger events.
   */
  collect(id: string): void {
    const parsed = this.parseId(id);
    if (!parsed) return;

    const { type, value } = parsed;

    // Check if already collected (idempotent)
    if (type === 'letter') {
      if (this._letters.get(value) === true) return;
      this._letters.set(value, true);
    } else {
      if (this._numbers.get(value) === true) return;
      this._numbers.set(value, true);
    }

    // Build the collected item data
    const item: CollectibleItem = {
      id,
      type,
      value,
      collected: true,
    };

    // Emit 'collected' event
    this.emit('collected', { event: 'collected', item });

    // Check power-up triggers
    this.checkPowerUpTriggers();
  }

  /**
   * Reset all collectible state back to initial (nothing collected).
   */
  reset(): void {
    this._vowelsComplete = false;
    this._numbersComplete = false;
    this._alphabetComplete = false;
    this.initializeMaps();
  }

  // ─── Events ─────────────────────────────────────────────────────────────────

  /**
   * Register an event handler for collectible events.
   */
  on(event: CollectibleEvent, handler: CollectibleEventHandler): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.add(handler);
    }
  }

  /**
   * Remove an event handler for collectible events.
   */
  off(event: CollectibleEvent, handler: CollectibleEventHandler): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(handler);
    }
  }

  // ─── Persistence ────────────────────────────────────────────────────────────

  /**
   * Serialize current state to a saveable format.
   */
  serialize(): CollectibleSaveData {
    const collectedLetters: string[] = [];
    const collectedNumbers: string[] = [];

    for (const [letter, collected] of this._letters) {
      if (collected) collectedLetters.push(letter);
    }
    for (const [number, collected] of this._numbers) {
      if (collected) collectedNumbers.push(number);
    }

    return { collectedLetters, collectedNumbers };
  }

  /**
   * Deserialize saved state, restoring collection progress.
   * Recalculates power-up trigger states without emitting events.
   */
  deserialize(data: CollectibleSaveData): void {
    // Reset maps first
    this.initializeMaps();

    // Restore letters
    for (const letter of data.collectedLetters) {
      if (this._letters.has(letter)) {
        this._letters.set(letter, true);
      }
    }

    // Restore numbers
    for (const number of data.collectedNumbers) {
      if (this._numbers.has(number)) {
        this._numbers.set(number, true);
      }
    }

    // Recalculate trigger states (no events emitted during deserialization)
    this._vowelsComplete = this.areVowelsComplete();
    this._numbersComplete = this.areNumbersComplete();
    this._alphabetComplete = this.isAlphabetComplete();
  }

  // ─── Private Methods ────────────────────────────────────────────────────────

  /**
   * Initialize the letter and number maps with all items set to uncollected.
   */
  private initializeMaps(): void {
    this._letters = new Map();
    for (const letter of ALPHABET) {
      this._letters.set(letter, false);
    }

    this._numbers = new Map();
    for (const num of NUMBERS) {
      this._numbers.set(num, false);
    }
  }

  /**
   * Parse a collectible ID into type and value.
   * Returns null if the ID format is invalid.
   */
  private parseId(id: string): { type: CollectibleType; value: string } | null {
    if (id.startsWith('letter-')) {
      const value = id.slice(7); // "letter-".length = 7
      if (ALPHABET.includes(value)) {
        return { type: 'letter', value };
      }
    } else if (id.startsWith('number-')) {
      const value = id.slice(7); // "number-".length = 7
      if (NUMBERS.includes(value)) {
        return { type: 'number', value };
      }
    }
    return null;
  }

  /**
   * Check all power-up trigger conditions after a collection.
   * Emits events only on transition from incomplete → complete.
   */
  private checkPowerUpTriggers(): void {
    // Check vowels complete
    if (!this._vowelsComplete && this.areVowelsComplete()) {
      this._vowelsComplete = true;
      this.emit('vowels-complete', { event: 'vowels-complete' });
    }

    // Check numbers complete
    if (!this._numbersComplete && this.areNumbersComplete()) {
      this._numbersComplete = true;
      this.emit('numbers-complete', { event: 'numbers-complete' });
    }

    // Check alphabet complete
    if (!this._alphabetComplete && this.isAlphabetComplete()) {
      this._alphabetComplete = true;
      this.emit('alphabet-complete', { event: 'alphabet-complete' });
    }
  }

  /**
   * Check if all vowels (A, E, I, O, U) are collected.
   */
  private areVowelsComplete(): boolean {
    return VOWELS.every((v) => this._letters.get(v) === true);
  }

  /**
   * Check if all numbers (1-9) are collected.
   */
  private areNumbersComplete(): boolean {
    return NUMBERS.every((n) => this._numbers.get(n) === true);
  }

  /**
   * Check if all letters (A-Z) are collected.
   */
  private isAlphabetComplete(): boolean {
    return ALPHABET.every((l) => this._letters.get(l) === true);
  }

  /**
   * Emit an event to all registered handlers.
   */
  private emit(event: CollectibleEvent, data: CollectibleEventData): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      for (const handler of listeners) {
        handler(data);
      }
    }
  }
}
